import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AUTH_CONFIG } from './auth-config';
import { AuthService } from './auth';

/** Requires a non-expired authenticated session (silent refresh when possible). */
export const authGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const config = inject(AUTH_CONFIG);

  if (!(await ensureSession(auth))) {
    return router.createUrlTree([config.loginUrl], {
      queryParams: loginRedirectQuery(router, state.url),
    });
  }

  if (auth.needsTotpEnrollment() && !state.url.startsWith(config.totpSetupUrl)) {
    return router.createUrlTree([config.totpSetupUrl]);
  }

  return true;
};

/** Blocks the login page when already signed in. */
export const guestGuard: CanActivateFn = async (route) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const config = inject(AUTH_CONFIG);

  if (!(await ensureSession(auth))) {
    return true;
  }

  // Deep links use /login?tenantId=… to switch tenants. One browser profile
  // holds a single Platform/Inventory session per app — clear it and re-auth.
  const requestedTenant = (
    route.queryParamMap.get('tenantId') ??
    route.queryParamMap.get('tenant') ??
    ''
  ).trim();
  if (requestedTenant && !sameTenant(requestedTenant, auth.tenantId(), auth.locationCode())) {
    auth.logout(/* navigateToLogin */ false);
    if (route.queryParamMap.get('replaceSession') === '1') {
      return true;
    }
    const queryParams: Record<string, string> = {
      tenantId: requestedTenant,
      replaceSession: '1',
    };
    const returnUrl = safeReturnUrl(route.queryParamMap.get('returnUrl'));
    if (returnUrl) {
      queryParams['returnUrl'] = returnUrl;
    }
    return router.createUrlTree([config.loginUrl], { queryParams });
  }

  if (auth.needsTotpEnrollment()) {
    return router.createUrlTree([config.totpSetupUrl]);
  }

  const returnUrl = safeReturnUrl(route.queryParamMap.get('returnUrl'));
  return router.parseUrl(returnUrl ?? '/');
};

/**
 * Requires one or more permission scopes.
 * Route data:
 *   permission?: string
 *   permissions?: string[]
 *   permissionMode?: 'any' | 'all'  (default 'any')
 */
export const permissionGuard: CanActivateFn = async (route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const config = inject(AUTH_CONFIG);

  if (!(await ensureSession(auth))) {
    return router.createUrlTree([config.loginUrl], {
      queryParams: loginRedirectQuery(router, state.url),
    });
  }

  if (auth.needsTotpEnrollment()) {
    return router.createUrlTree([config.totpSetupUrl]);
  }

  const single = route.data['permission'] as string | undefined;
  const many = (route.data['permissions'] as string[] | undefined) ?? [];
  const required = single ? [single, ...many] : many;

  if (required.length === 0) {
    return true;
  }

  const mode = (route.data['permissionMode'] as 'any' | 'all' | undefined) ?? 'any';
  const allowed = mode === 'all' ? auth.canAll(required) : auth.canAny(required);

  if (allowed) {
    return true;
  }

  return router.createUrlTree([config.forbiddenUrl]);
};

async function ensureSession(auth: AuthService): Promise<boolean> {
  if (auth.ensureValidSession()) {
    return true;
  }

  if (!auth.hasRefreshableSession()) {
    return false;
  }

  try {
    await firstValueFrom(auth.refreshSession());
    return auth.ensureValidSession();
  } catch {
    return false;
  }
}

function loginRedirectQuery(router: Router, attemptedUrl: string): Record<string, string> {
  const queryParams: Record<string, string> = { returnUrl: attemptedUrl };
  const tenantId = router.parseUrl(attemptedUrl).queryParams['tenantId'];
  if (typeof tenantId === 'string' && tenantId.trim()) {
    queryParams['tenantId'] = tenantId.trim();
  }
  return queryParams;
}

/** Only allow same-origin relative paths (blocks open redirects). */
function safeReturnUrl(raw: string | null): string | null {
  if (!raw?.startsWith('/') || raw.startsWith('//')) {
    return null;
  }
  return raw;
}

function sameTenant(
  requested: string,
  current: string | null,
  locationCode: string | null = null,
): boolean {
  if (!current) {
    return false;
  }
  const a = requested.trim();
  const b = current.trim();
  if (!a || !b) {
    return false;
  }
  if (a.toLowerCase() === b.toLowerCase()) {
    return true;
  }
  // GUID vs compact N-form (no hyphens).
  const compact = (value: string) => value.replace(/-/g, '').toLowerCase();
  if (compact(a).length === 32 && compact(a) === compact(b)) {
    return true;
  }
  // Location code (e.g. 001) vs session GUID — match when login stored the code.
  const requestedCode = normalizeLocationCode(a);
  const sessionCode = normalizeLocationCode(locationCode);
  return !!requestedCode && !!sessionCode && requestedCode === sessionCode;
}

/** 3-character base-36 location code (e.g. 001), or null when not a code. */
function normalizeLocationCode(value: string | null | undefined): string | null {
  const trimmed = value?.trim().toUpperCase() ?? '';
  if (!/^[0-9A-Z]{3}$/.test(trimmed)) {
    return null;
  }
  return trimmed;
}
